import {
  Box,
  HStack,
  Icon,
  Center,
  InputGroup,
  InputLeftElement,
  Tag,
  TagLabel,
  TagCloseButton,
  Input,
  InputRightElement,
  IconButton,
  Flex,
  Button,
  Text,
  Divider,
} from '@chakra-ui/react'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import { parse, stringify } from 'query-string'
import type { RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean, useWhyDidYouUpdate } from '@chakra-ui/hooks'
import { IoClose, IoSearch } from 'react-icons/io5'
import type { TFunction } from 'i18next'
import type { Interceptors } from 'use-http'
import useFetch, { CachePolicies } from 'use-http'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { Base64 } from 'js-base64'
import { TbAdjustmentsHorizontal, TbPhotoSearch, TbX } from 'react-icons/tb'
import type { UseComboboxReturnValue } from 'downshift'
import { colors } from '../../../../ui-kit'

import { MLContext, UserContext } from '../../../providers'
import { FileType, MLSortDirection, MLSortParams } from '../../../constants'
import type {
  CustomMetadataResponse,
  FormValidationObject,
  ListItemProps,
  SearchQueryJson,
} from '../../../interfaces'
import {
  encodeStringForUrl,
  extractSingleField,
  fileTypeMapping,
  fileTypeOptionsMapping,
  getSearchQueryFromJSON,
  validateFilter,
} from '../../../../utils/mediaLibrary'
import type { MediaLibraryActionType } from '../../../providers/MLProvider'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import { CustomMetadataContext } from '../../../providers/CustomMetadataProvider'
import SearchMenu from './SearchMenu'
import { FeaturesEnum } from '../../../../utils/features'

const Dimmer = ({
  isDimmed,
  node,
}: {
  isDimmed: boolean
  node?: HTMLElement
}): React.ReactElement | null =>
  isDimmed && node
    ? ReactDOM.createPortal(
        <Box
          className="dimmer"
          position="fixed"
          h="100vh"
          w="100vw"
          top="0"
          left="0"
          bgColor="blackAlpha.600"
          zIndex="overlay"
        />,
        node,
      )
    : null

interface Props {
  searchFocused: boolean
  setSearchFocused(val: boolean): void
  resetSearch(): void
}

const Search = ({
  searchFocused,
  setSearchFocused,
  resetSearch,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const [state, dispatch] = useContext(MLContext)
  const history = useHistory()

  /* ************************  STATE ******************** */
  const [isNameInputDisabled, setIsNameInputDisabled] = useBoolean(false)
  const [isFileTypeMenuDisabled, setIsFileTypeMenuDisabled] = useBoolean(false)
  const [nameValue, setNameValue] = useState<string>(state.nameValue)
  const [isFolderRestrictedSearch, setIsFolderRestrictedSearch] =
    useState<string>(state.isFolderRestrictedSearch)
  const [fileType, setFileType] = useState<{
    text: TFunction
    value: FileType
  }>({
    text: t(fileTypeOptionsMapping[state.fileType]),
    value: state.fileType,
  })
  const [tempFileType, setTempFileType] = useState<{
    text: TFunction
    value: FileType
  }>({
    text: t(fileTypeOptionsMapping[state.fileType]),
    value: state.fileType,
  })
  const [versionIncluded, setVersionIncluded] = useState<boolean>(
    state.isVersionIncludedInSearch,
  )
  useState<boolean>(false)
  const [searchQueryJson, setSearchQueryJson] = useState<SearchQueryJson>(
    state.searchQueryJson,
  )
  useState<boolean>(false)
  const [inputBoxText, setInputBoxText] = useState<string | undefined>()
  const [semanticSearch, setSemanticSearch] = useState<string>(state.semantic)
  /* ************************  STATE END ******************** */

  /* ***************** CUSTOM METADATA FIELDS FETCH ****************** */
  let { cmFields: customMetadataFields } = useContext(CustomMetadataContext)

  customMetadataFields = customMetadataFields.map(x => ({
    ...x,
    name: `"customMetadata.${x.name}"`,
  }))

  const customMetadataFieldsMapping: {
    [fieldName: string]: CustomMetadataResponse
  } = useMemo(() => {
    const ret: {
      [fieldName: string]: CustomMetadataResponse
    } = {}
    customMetadataFields.forEach(customMetadataField => {
      ret[customMetadataField.name] = customMetadataField
    })
    return ret
  }, [customMetadataFields])
  /* ************************* CUSTOM METADATA FIELDS FETCH END ********************* */

  /* ****************** CONSTANTS **************** */
  const getInputBoxText = useCallback((): string => {
    if (searchQueryJson.filterGroups.length > 0) {
      return t('custom_search_filters_capitalized', 'Custom search filters')
    }

    if (nameValue !== '') {
      return nameValue
    }

    if (semanticSearch !== '' && semanticSearch !== undefined) {
      return semanticSearch
    }

    if (state.isVersionIncludedInSearch) {
      return t('file_versions', 'File versions')
    }

    if (!state.openAdvancedSearch) {
      return t('start_typing_to_search', 'Start typing to search')
    }

    return t('file_or_folder_name', 'File or folder name')
  }, [
    searchQueryJson.filterGroups,
    nameValue,
    semanticSearch,
    state.isVersionIncludedInSearch,
    state.openAdvancedSearch,
    isFolderRestrictedSearch,
  ])

  useEffect(() => {
    setInputBoxText(getInputBoxText())
  }, [nameValue, state.isVersionIncludedInSearch, getInputBoxText])

  useEffect(() => {
    if (state.isVersionIncludedInSearch !== versionIncluded) {
      setVersionIncluded(state.isVersionIncludedInSearch)
    }
  }, [state.isVersionIncludedInSearch])

  const fileTypeOptions: Array<{
    text: TFunction
    value: FileType
  }> = useMemo(
    () => [
      {
        text: t('all', 'All'),
        value: FileType.all,
      },
      {
        text: t('images', 'Images'),
        value: FileType.images,
      },
      {
        text: t('videos', 'Videos'),
        value: FileType.videos,
      },
      {
        text: t('css_js', 'CSS/JS'),
        value: FileType.cssJs,
      },
      {
        text: t('others', 'Others'),
        value: FileType.others,
      },
    ],
    [],
  )

  const validationObject: FormValidationObject = useMemo(() => {
    let isValid = true
    const errors: Array<Array<string | null>> = []

    searchQueryJson.filterGroups.forEach((filterGroup, groupIndex) => {
      errors.push([])
      filterGroup.expressions.forEach((expression, filterIndex) => {
        const validation = validateFilter({
          expression,
          customMetadataField:
            customMetadataFieldsMapping[expression.fieldName],
        })
        if (!validation.isValid) {
          isValid = false
        }
        errors[groupIndex][filterIndex] = validation.error
      })
    })

    return {
      isValid,
      errors,
    }
  }, [searchQueryJson, customMetadataFieldsMapping])

  const inputBoxLeftElement = useMemo<React.ReactElement>(() => {
    if (
      (fileType.value === FileType.all && !searchFocused) ||
      (tempFileType.value === FileType.all && searchFocused)
    ) {
      return (
        <Center width="2.5rem">
          <Icon color="#9DA3AE" boxSize="1.25rem" as={IoSearch} />
        </Center>
      )
    }
    return (
      <Tag padding="0.5em" marginLeft="0.5em">
        <TagLabel color={colors.appSlate}>
          {searchFocused ? tempFileType.text : fileType.text}
        </TagLabel>
        <TagCloseButton
          onClick={() => {
            setTempFileType({
              text: t('all', 'All'),
              value: FileType.all,
            })
          }}
        />
      </Tag>
    )
  }, [fileType, searchFocused, tempFileType])

  // added searchFocused in the condition for the cross icon to appear only when the search is not focused, previously this was achieved by sending null in the inputBoxRightElement
  const inputBoxRightElement = useMemo<React.ReactElement | null>(
    () => (
      <Flex align="center">
        {(fileType.value === FileType.all &&
          nameValue === '' &&
          semanticSearch === '' &&
          searchQueryJson.filterGroups.length === 0 &&
          !state.isVersionIncludedInSearch) ||
        !state.openAdvancedSearch ||
        searchFocused ? null : (
          <Button
            cursor="pointer"
            variant="ghost"
            onClick={() => {
              // explain: since the cross icon functionality for auto completer is different from the cross icon functionality for advanced search, we can drop all the if conditions and added codes
              // void setNameValue('')
              // if (!searchFocused) {
              void handleReset()
              // question: should we reset the fileType to all when the cross icon is clicked? Is it necessary?
              // void setTempFileType({
              //   value: FileType.all,
              //   text: t('all', 'All'),
              // })
              // }
              // explain: since we are already checking for state.openAdvancedSearch in the if condition above, it is redundant to check it again here
              // if (state.openAdvancedSearch) {
              //   void setSearchFocused(false)
              // }
              setSearchFocused(false)
            }}
          >
            <Text color="appBattleshipGrey" fontWeight="normal">
              {t('clear_search', 'clear')}
            </Text>
          </Button>
        )}
        <IconButton
          aria-label="Advanced Search"
          icon={<TbAdjustmentsHorizontal />}
          colorScheme={state.openAdvancedSearch ? colors.appBlue : '#9DA3AE'}
          fontSize="1.25em"
          onClick={() => {
            dispatch([
              {
                type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
                payload: true,
              },
            ])
          }}
          variant="outline"
          border="none"
          borderRadius="0"
          borderColor={colors.appPaleLilac}
        />
        {user?.pricingPlan.featuresList.includes(
          FeaturesEnum.SEMANTIC_SEARCH,
        ) ? (
          <IconButton
            aria-label="Visual Search"
            icon={<TbPhotoSearch />}
            colorScheme={
              state.openSemanticImageSearch ? colors.appBlue : '#9DA3AE'
            }
            fontSize="1.25em"
            onClick={() => {
              dispatch([
                {
                  type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
                  payload: true,
                },
              ])
            }}
            variant="outline"
            border="none"
            borderRadius="0"
            borderColor={colors.appPaleLilac}
          />
        ) : (
          <></>
        )}
      </Flex>
    ),
    [
      fileType,
      nameValue,
      semanticSearch,
      inputBoxText,
      state.isVersionIncludedInSearch,
      state.openAdvancedSearch,
      state.openSemanticImageSearch,
      searchQueryJson,
      searchFocused,
    ],
  )

  const searchInputRef = useRef(null)

  const searchInputActualRef = useRef(null)

  const { url } = useRouteMatch()

  /* ****************** CONSTANTS END **************** */

  /* ************************* CALLBACKS ********************** */
  const onSubmitSearch = useCallback(() => {
    setInputBoxText(getInputBoxText())
    let fileTypeQuery = ''
    if (fileType.value !== FileType.all && !isFileTypeMenuDisabled) {
      const fileTypeQueryOperator =
        fileType.value === FileType.others ? 'NOT IN' : 'IN'
      fileTypeQuery = `(format ${fileTypeQueryOperator} [${fileTypeMapping[
        fileType.value
      ]
        .map(x => `"${x}"`)
        .join(',')}])`
    }

    let nameQuery = ''
    if (nameValue && !isNameInputDisabled) {
      nameQuery = `(name HAS "${nameValue}")`
    }

    let folderRestrictedSearchQuery = ''
    if (isFolderRestrictedSearch === 'true') {
      folderRestrictedSearchQuery = `(path : "${state.folderPath}")`
    }

    let searchQuery = getSearchQueryFromJSON({
      searchQueryJson,
      customMetadataFieldsMapping,
    })
    if (fileTypeQuery) {
      searchQuery = searchQuery
        ? `${searchQuery} AND ${fileTypeQuery}`
        : fileTypeQuery
    }
    if (nameQuery) {
      searchQuery = searchQuery ? `${searchQuery} AND ${nameQuery}` : nameQuery
    }

    if (folderRestrictedSearchQuery && searchQuery) {
      searchQuery = `${searchQuery} AND ${folderRestrictedSearchQuery}`
    } else if (
      folderRestrictedSearchQuery &&
      !searchQuery &&
      state.isVersionIncludedInSearch
    ) {
      searchQuery = folderRestrictedSearchQuery
    }

    const semanticSearchPayload: MediaLibraryActionType[] = [
      {
        type: MediaLibraryActions.SET_SEARCH_SEMANTIC_VALUE,
        payload: semanticSearch,
      },
      {
        type: MediaLibraryActions.SET_SEARCH_SEMANTIC_URL_VALUE,
        payload: '',
      },
    ]

    if (semanticSearch) {
      semanticSearchPayload.push({
        type: MediaLibraryActions.SET_SORT_PARAM,
        payload: MLSortParams.RELEVANCE,
      })
    }

    dispatch([
      {
        type: MediaLibraryActions.SET_SEARCH_NAME_VALUE,
        payload: nameValue,
      },
      {
        type: MediaLibraryActions.SET_FOLDER_RESTRICTED_SEARCH,
        payload: isFolderRestrictedSearch,
      },
      ...semanticSearchPayload,
      {
        type: MediaLibraryActions.SET_SEARCH_FILE_TYPE,
        payload: fileType.value,
      },
      {
        type: MediaLibraryActions.SET_FILE_TYPE_QUERY,
        payload: state.isVersionIncludedInSearch
          ? 'type IN ["file", "file-version"]'
          : 'type="file"',
      },
      {
        type: MediaLibraryActions.SET_SEARCH_QUERY,
        payload: searchQuery,
      },
      {
        type: MediaLibraryActions.SET_SEARCH_QUERY_JSON,
        payload: searchQueryJson,
      },
    ])

    let shouldRefresh = false

    if (
      state.searchQuery === searchQuery &&
      state.semantic === semanticSearch
    ) {
      if (
        state.isVersionIncludedInSearch &&
        state.fileTypeQuery === 'type IN ["file", "file-version"]'
      ) {
        shouldRefresh = true
      }

      if (
        !state.isVersionIncludedInSearch &&
        state.fileTypeQuery === 'type="file"'
      ) {
        shouldRefresh = true
      }
    }

    // if searchQuery is same as before, then useMLSearch hook won't fire request automatically
    if (shouldRefresh) {
      void state.refetch()
    }

    const defaultQueryparams = {
      searchQuery: '',
      nameValue: '',
      fileType: FileType.all,
      searchQueryJson: encodeStringForUrl(
        JSON.stringify({
          boolean: 'and',
          filterGroups: [],
        }),
      ),
      type: Base64.encode('type="file"', true),
      isFolderRestrictedSearch: 'false',
    }

    const preTypeValue = Base64.encode(
      state.isVersionIncludedInSearch
        ? 'type IN ["file", "file-version"]'
        : 'type="file"',
      true,
    )

    const preSearchQueryJsonVlaue = encodeStringForUrl(
      JSON.stringify(searchQueryJson),
    )

    const queryParams = Object.entries(defaultQueryparams).reduce(
      (acc, [key, value]) => {
        if (key === 'searchQuery' && defaultQueryparams[key] !== searchQuery) {
          acc[key] = searchQuery
        }

        if (key === 'nameValue' && defaultQueryparams[key] !== nameValue) {
          acc[key] = nameValue
        }

        if (key === 'fileType' && defaultQueryparams[key] !== fileType.value) {
          acc[key] = fileType.value
        }

        if (
          key === 'searchQueryJson' &&
          defaultQueryparams[key] !== preSearchQueryJsonVlaue
        ) {
          acc[key] = preSearchQueryJsonVlaue
        }

        if (key === 'type' && defaultQueryparams[key] !== preTypeValue) {
          acc[key] = preTypeValue
        }

        if (
          key === 'isFolderRestrictedSearch' &&
          defaultQueryparams[key] !== isFolderRestrictedSearch
        ) {
          acc[key] = isFolderRestrictedSearch
        }

        return acc
      },
      {},
    )

    if (state.mediaCollectionRoute) {
      history.replace({
        pathname: `/media-library/${Base64.encode(state.folderPath, true)}`,
        search: stringify(queryParams, { skipNull: true }),
      })
    }
  }, [
    searchQueryJson,
    fileType,
    nameValue,
    state.searchQuery,
    state.fileTypeQuery,
    state.refetch,
    url,
    semanticSearch,
    isFolderRestrictedSearch,
    state.isVersionIncludedInSearch,
    state.folderPath,
  ])

  const removeFilter = useCallback(
    ({
      json,
      groupId,
      filterId,
    }: {
      json: SearchQueryJson
      groupId: number
      filterId: number
    }) => {
      const newSearchQueryJSON = { ...json }
      newSearchQueryJSON.filterGroups[groupId].expressions.splice(filterId, 1)
      const totalFilters =
        newSearchQueryJSON.filterGroups[groupId].expressions.length
      if (totalFilters === 0) {
        newSearchQueryJSON.filterGroups.splice(groupId, 1)
      }
      setSearchQueryJson(newSearchQueryJSON)
    },
    [onSubmitSearch, setSearchQueryJson],
  )
  /* ************************* CALLBACKS END ********************** */

  /* ************************ EFFECTS ************************ */
  useEffect(() => {
    const foundNameValue = extractSingleField(searchQueryJson, 'name') as
      | string
      | undefined
    if (typeof foundNameValue !== 'undefined') {
      setIsNameInputDisabled.on()
      setNameValue('')
    } else {
      setIsNameInputDisabled.off()
    }

    const formatValue = extractSingleField(searchQueryJson, 'format') as
      | string
      | undefined
    if (typeof formatValue !== 'undefined') {
      setIsFileTypeMenuDisabled.on()
      setFileType({
        text: t('all', 'All'),
        value: FileType.all,
      })
    } else {
      setIsFileTypeMenuDisabled.off()
    }
  }, [searchQueryJson])

  useEffect(() => {
    setNameValue(state.nameValue)
    setSemanticSearch(state.semantic)
    setSearchQueryJson(state.searchQueryJson)
    setFileType({
      text: t(fileTypeOptionsMapping[state.fileType]),
      value: state.fileType,
    })
  }, [state.searchQueryJson, state.nameValue, state.fileType, state.semantic])

  useEffect(() => {
    if (
      tempFileType.value !== FileType.all ||
      state.searchQueryJson.filterGroups.length > 0 ||
      state.fileType !== FileType.all
    ) {
      dispatch([
        {
          type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
          payload: true,
        },
      ])
    }
  }, [state.searchQueryJson, state.nameValue, state.fileType, tempFileType])

  useEffect(() => {
    setIsFolderRestrictedSearch(state.isFolderRestrictedSearch)
  }, [state.isFolderRestrictedSearch])
  /* ************************ EFFECTS END ************************ */

  useEffect(() => {
    setTempFileType(fileType)
  }, [fileType])

  useEffect(() => {
    if (searchQueryJson.filterGroups.length === 0) {
      setInputBoxText(getInputBoxText())
    }
  }, [searchQueryJson])

  const handleReset = useCallback(() => {
    setTempFileType({
      text: t('all', 'All'),
      value: FileType.all,
    })
    setVersionIncluded(false)
    setSearchQueryJson(prev => ({ ...searchQueryJson, filterGroups: [] }))
    setIsFolderRestrictedSearch('false')
    resetSearch()
  }, [resetSearch, setVersionIncluded, setTempFileType, searchQueryJson])

  if (!customMetadataFields) {
    return <></>
  }

  return (
    <>
      <HStack
        ref={searchInputRef}
        spacing="0"
        cursor="pointer"
        zIndex={1401}
        onClick={() => void setSearchFocused(true)}
        height="35px"
        width="full"
        bgColor={colors.appWhite}
        paddingEnd={10}
      >
        <InputGroup
          display={searchFocused ? 'none' : 'flex'}
          ref={searchInputActualRef}
        >
          <InputLeftElement
            children={inputBoxLeftElement}
            paddingLeft="0.5em"
            position="static"
            width="unset"
            borderWidth="1px"
            borderColor="#e6e7ea"
            borderRadius="0.25rem"
            borderRightWidth="0"
            borderRightRadius="0"
            cursor={fileType.value !== FileType.all ? 'default' : 'pointer'}
          />
          <Input
            isReadOnly
            cursor="pointer"
            placeholder={inputBoxText}
            value={undefined}
            focusBorderColor="none"
            _hover={{ borderColor: '#e6e7ea' }}
            borderLeftRadius="0"
            borderLeftWidth="0"
            position="static"
            paddingLeft={fileType.value !== FileType.all ? '0.5em' : 'unset'}
          />
          {inputBoxRightElement ? (
            <InputRightElement width="unset" children={inputBoxRightElement} />
          ) : null}
        </InputGroup>
      </HStack>

      {searchFocused && (
        <SearchMenu
          {...{
            searchInputRef,
            searchInputActualRef,
            nameValue,
            isFolderRestrictedSearch,
            fileType: fileType.value,
            tempFileType,
            setNameValue,
            setIsFolderRestrictedSearch,
            onSubmitSearch,
            isNameInputDisabled,
            isFileTypeMenuDisabled,
            fileTypeOptions,
            inputBoxLeftElement,
            inputBoxRightElement: !(
              state.openAdvancedSearch || state.openSemanticImageSearch
            )
              ? inputBoxRightElement
              : null,
            searchQueryJson,
            setSearchQueryJson,
            customMetadataFields,
            customMetadataFieldsMapping,
            validationObject,
            removeFilter,
            setFileType,
            setTempFileType,
            setSearchFocused,
            resetSearch: handleReset,
            versionIncluded,
            setVersionIncluded,
            semanticSearch,
            setSemanticSearch,
          }}
        />
      )}

      <Dimmer node={document.body} isDimmed={searchFocused} />
    </>
  )
}

export default Search
